// import { TablePaginationConfig } from 'antd';
// import { useCallback, useEffect } from 'react';
// import { useSearchParams } from 'react-router-dom';
// import { isObjectEmpty } from '../../auth/utils/lib';
// import useUrlParams from './useUrlParams';

// export interface Pagination {
//   current: number;
//   pageSize: number;
// }

// interface UsePaginationProps {
//   search?: string;
//   defaultPageSize?: number;
// }

// interface UsePaginationReturn {
//   pagination: Pagination;
//   handlePaginationChange: (current: number, pageSize: number) => void;
//   resetPagination: () => void;
//   limit: number;
//   skip: number;
//   changePagination: any;
// }

// const usePagination = ({
//   search = '',
//   defaultPageSize = 100,
// }: UsePaginationProps = {}): UsePaginationReturn => {
//   const [searchParams, setSearchParams] = useSearchParams();

//   const { getAllUrlParams } = useUrlParams();
//   const ifParams = !isObjectEmpty(getAllUrlParams());

//   const getPaginationFromParams = (): Pagination => {
//     const current = parseInt(searchParams.get('current') || '1', 10);
//     const pageSize = parseInt(searchParams.get('pageSize') || defaultPageSize?.toString(), 10);
//     return { current, pageSize };
//   };

//   const setPaginationToParams = (pagination: Pagination): void => {
//     const newParams = new URLSearchParams(searchParams);
//     newParams.set('current', pagination.current?.toString());
//     newParams.set('pageSize', pagination.pageSize?.toString());
//     setSearchParams(newParams);
//   };

//   const pagination = getPaginationFromParams();
//   const limit = pagination.pageSize;
//   const skip = (pagination.current - 1) * pagination.pageSize;

//   const handlePaginationChange = useCallback(
//     (current: number, pageSize: number) => {
//       const newPagination = { current, pageSize };
//       setPaginationToParams(newPagination);
//     },
//     [searchParams, setSearchParams]
//   );

//   const resetPagination = useCallback(() => {
//     const defaultPagination = { current: 1, pageSize: defaultPageSize };
//     setPaginationToParams(defaultPagination);
//   }, [defaultPageSize, setSearchParams]);

//   const changePagination = (total?: number): false | TablePaginationConfig | undefined => {
//     return total !== undefined && total < defaultPageSize
//       ? false
//       : {
//           ...pagination,
//           total: total,
//           showSizeChanger: true,
//           pageSizeOptions: ['100', '300', '500'],
//           onChange: handlePaginationChange,
//           size: 'small',
//         };
//   };

//   useEffect(() => {
//     if (search && ifParams) {
//       resetPagination();
//     }
//   }, [search, resetPagination, ifParams]);

//   return {
//     pagination,
//     handlePaginationChange,
//     resetPagination,
//     limit,
//     skip,
//     changePagination,
//   };
// };

// export default usePagination;

import { TablePaginationConfig } from 'antd';
import { useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { isObjectEmpty } from '../../auth/utils/lib';
import useUrlParams from './useUrlParams';

export interface Pagination {
  current: number;
  pageSize: number;
}

interface UsePaginationProps {
  search?: string;
  defaultPageSize?: number;
}

interface UsePaginationReturn {
  pagination: Pagination;
  handlePaginationChange: (current: number, pageSize: number) => void;
  resetPagination: () => void;
  page: number;
  size: number;
  changePagination: any;
}

const usePagination = ({
  search = '',
  defaultPageSize = 100,
}: UsePaginationProps = {}): UsePaginationReturn => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { getAllUrlParams } = useUrlParams();
  const ifParams = !isObjectEmpty(getAllUrlParams());

  const getPaginationFromParams = (): Pagination => {
    const current = parseInt(searchParams.get('page') || '1', 10); // Changed from 'current' to 'page'
    const pageSize = parseInt(searchParams.get('size') || defaultPageSize?.toString(), 10); // Changed from 'pageSize' to 'size'
    return { current, pageSize };
  };

  const setPaginationToParams = (pagination: Pagination): void => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('page', pagination.current?.toString()); // Changed from 'current' to 'page'
    newParams.set('size', pagination.pageSize?.toString()); // Changed from 'pageSize' to 'size'
    setSearchParams(newParams);
  };

  const pagination = getPaginationFromParams();
  const page = pagination.current; // Changed from 'limit' to 'page'
  const size = pagination.pageSize; // Changed from 'skip' to 'size'

  const handlePaginationChange = useCallback(
    (current: number, pageSize: number) => {
      const newPagination = { current, pageSize };
      setPaginationToParams(newPagination);
    },
    [searchParams, setSearchParams]
  );

  const resetPagination = useCallback(() => {
    const defaultPagination = { current: 1, pageSize: defaultPageSize };
    setPaginationToParams(defaultPagination);
  }, [defaultPageSize, setSearchParams]);

  const changePagination = (total?: number): false | TablePaginationConfig | undefined => {
    return total !== undefined && total < defaultPageSize
      ? false
      : {
          ...pagination,
          total: total,
          showSizeChanger: true,
          pageSizeOptions: ['100', '300', '500'],
          onChange: handlePaginationChange,
          size: 'small',
        };
  };

  useEffect(() => {
    if (search && ifParams) {
      resetPagination();
    }
  }, [search, resetPagination, ifParams]);

  return {
    pagination,
    handlePaginationChange,
    resetPagination,
    page,
    size,
    changePagination,
  };
};

export default usePagination;
